import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, IconButton,
  Checkbox, FormControlLabel, Typography
} from "@mui/material";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { addMenuToUser, deleteMenuFromUser, editMenuFromUser } from "../../services/authService";
import * as lucideIcons from "lucide-react";
import { ArrowBack, ArrowForward } from "@mui/icons-material";

interface EditViewModalProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  selectedMenu: Menu | null;
}

const ItemTypes = {
  VIEW: 'view'
};

type IconName = keyof typeof lucideIcons;

const EditViewModal: React.FC<EditViewModalProps> = ({ open, onClose, user, selectedMenu }) => {
  const [menuName, setMenuName] = useState("");
  const [views, setViews] = useState<View[]>([]);
  const [viewName, setViewName] = useState("");
  const [viewLink, setViewLink] = useState("");
  const [viewIcon, setViewIcon] = useState("");
  const [menuIcon, setMenuIcon] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditable, setIsEditable] = useState(false);
  const [sheetsId, setSheetsId] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [reportId, setReportId] = useState("");
  const iconsPerPage = 25;

  useEffect(() => {
    if (selectedMenu) {
      setMenuName(selectedMenu.menuName);
      setViews(selectedMenu.views);
      setMenuIcon(selectedMenu.icon || "");
    } else {
      setMenuName("");
      setViews([]);
      setMenuIcon("");
    }
  }, [selectedMenu]);

  const handleSave = async () => {
    const updatedMenu = {
      menuName,
      views: views,
      icon: menuIcon
    };

    if (!selectedMenu) {
      await addMenuToUser(user!._id, updatedMenu);
    } else {
      await editMenuFromUser(user!._id, updatedMenu);
    }

    setMenuName("");
    setViews([]);
    setMenuIcon("");
    onClose();
  };

  const handleDelete = async () => {
    await deleteMenuFromUser(user!._id, selectedMenu!.menuName);
    onClose();
  };

  const moveView = (dragIndex: number, hoverIndex: number) => {
    const newViews = [...views];
    const draggedView = newViews[dragIndex];

    newViews.splice(dragIndex, 1);
    newViews.splice(hoverIndex, 0, draggedView);

    setViews(newViews);
  };

  const ViewItem = ({ view, index }: { view: View, index: number }) => {
    const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.VIEW,
      item: { index },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    });

    const [, drop] = useDrop({
      accept: ItemTypes.VIEW,
      hover: (item: any) => {
        if (item.index !== index) {
          moveView(item.index, index);
          item.index = index;
        }
      },
    });

    const IconComponent = view.icon ? (lucideIcons[view.icon as IconName] as React.ElementType) : null;

    return (
      <div
        ref={(node) => drag(drop(node))}
        style={{
          opacity: isDragging ? 0.5 : 1,
          cursor: 'move',
        }}
        className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] my-3 mx-4 items-center py-2 border-b border-gray-300 gap-2"
      >
        <div>{view.viewName}</div>
        <div className="max-w-full overflow-hidden break-words">{view.viewLink || "-"}</div>
        <div>{view.isEditable ? "Sí" : "No"}</div>
        <div>{view.sheetsId || "-"}</div>
        <div>{view.workspaceId || "-"}</div>
        <div>{view.reportId || "-"}</div>
        <div>{IconComponent && <IconComponent />}</div>
        <Button
          variant="contained"
          className="h-10"
          onClick={() => {
            const newViews = [...views];
            newViews.splice(index, 1);
            setViews(newViews);
          }}
        >
          Eliminar
        </Button>
      </div>
    );
  };

  const filteredIcons = Object.keys(lucideIcons).filter(iconName =>
    iconName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedIcons = filteredIcons.slice(currentPage * iconsPerPage, (currentPage + 1) * iconsPerPage);

  const handleAddView = () => {
    const newView: View = {
      viewName,
      icon: viewIcon,
      isEditable
    };

    if (isEditable) {
      newView.sheetsId = sheetsId;
      newView.workspaceId = workspaceId;
      newView.reportId = reportId;
    } else {
      newView.viewLink = viewLink;
    }

    const newViews: View[] = [...views, newView];
    setViews(newViews);

    // Reset form fields
    setViewName("");
    setViewLink("");
    setViewIcon("");
    setSheetsId("");
    setWorkspaceId("");
    setReportId("");
    setIsEditable(false);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>{selectedMenu ? "Editar Menu" : "Agregar Nuevo Menu"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nombre del Menu"
            margin="dense"
            value={menuName}
            onChange={(e) => setMenuName(e.target.value)}
          />

          <TextField
            fullWidth
            label="Buscar Icono"
            margin="dense"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="mt-3">
            <div className="flex flex-wrap gap-2">
              {paginatedIcons.map(iconName => {
                const IconComponent = lucideIcons[iconName as IconName] as React.ElementType;
                return (
                  <div
                    key={iconName}
                    onClick={() => setMenuIcon(iconName)}
                    style={{
                      cursor: 'pointer',
                      border: menuIcon === iconName ? '2px solid blue' : '2px solid transparent',
                      padding: '5px',
                      borderRadius: '5px'
                    }}
                  >
                    <IconComponent />
                    <div style={{ fontSize: '10px', textAlign: 'center' }}>{iconName}</div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between mt-3">
              <IconButton
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
              >
                <ArrowBack />
              </IconButton>
              <IconButton
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={(currentPage + 1) * iconsPerPage >= filteredIcons.length}
              >
                <ArrowForward />
              </IconButton>
            </div>
          </div>

          {menuIcon && (
            <div className="mt-3">
              <strong>Icono seleccionado para el menú:</strong> {menuIcon}
            </div>
          )}

          <Typography variant="h6" className="mt-4 mb-2">Vistas</Typography>

          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] my-3 mx-4 items-center py-2 border-b border-gray-300 font-bold gap-2">
            <div>Nombre Vista</div>
            <div>Enlace Vista</div>
            <div>Editable</div>
            <div>Sheets ID</div>
            <div>Workspace ID</div>
            <div>Report ID</div>
            <div>Icono</div>
            <div>Acciones</div>
          </div>

          {views.map((view, index) => (
            <ViewItem key={index} view={view} index={index} />
          ))}

          <Typography variant="h6" className="mt-6 mb-2">Agregar Nueva Vista</Typography>

          <div className="flex flex-col gap-3 mt-4">
            <div className="flex gap-3 items-center">
              <TextField
                fullWidth
                label="Nombre Vista"
                margin="dense"
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isEditable}
                    onChange={(e) => setIsEditable(e.target.checked)}
                  />
                }
                label="Es Editable"
              />
            </div>

            {!isEditable ? (
              <TextField
                fullWidth
                label="Enlace Vista"
                margin="dense"
                value={viewLink}
                onChange={(e) => setViewLink(e.target.value)}
                disabled={isEditable}
              />
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <TextField
                  fullWidth
                  label="Sheets ID"
                  margin="dense"
                  value={sheetsId}
                  onChange={(e) => setSheetsId(e.target.value)}
                  disabled={!isEditable}
                />
                <TextField
                  fullWidth
                  label="Workspace ID"
                  margin="dense"
                  value={workspaceId}
                  onChange={(e) => setWorkspaceId(e.target.value)}
                  disabled={!isEditable}
                />
                <TextField
                  fullWidth
                  label="Report ID"
                  margin="dense"
                  value={reportId}
                  onChange={(e) => setReportId(e.target.value)}
                  disabled={!isEditable}
                />
              </div>
            )}

            <div className="flex justify-end">
              <Button
                variant="contained"
                onClick={handleAddView}
                disabled={!viewName || (isEditable ? false : !viewLink) || (isEditable && (!sheetsId || !workspaceId || !reportId))}
              >
                Agregar Vista
              </Button>
            </div>
          </div>

          <TextField
            fullWidth
            label="Buscar Icono para Vista"
            margin="dense"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="mt-3">
            <div className="flex flex-wrap gap-2">
              {paginatedIcons.map(iconName => {
                const IconComponent = lucideIcons[iconName as IconName] as React.ElementType;
                return (
                  <div
                    key={iconName}
                    onClick={() => setViewIcon(iconName)}
                    style={{
                      cursor: 'pointer',
                      border: viewIcon === iconName ? '2px solid blue' : '2px solid transparent',
                      padding: '5px',
                      borderRadius: '5px'
                    }}
                  >
                    <IconComponent />
                    <div style={{ fontSize: '10px', textAlign: 'center' }}>{iconName}</div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between mt-3">
              <IconButton
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
              >
                <ArrowBack />
              </IconButton>
              <IconButton
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={(currentPage + 1) * iconsPerPage >= filteredIcons.length}
              >
                <ArrowForward />
              </IconButton>
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          {selectedMenu && (
            <Button color="secondary" disabled={!selectedMenu} onClick={handleDelete}>
              Eliminar
            </Button>
          )}

          <Button onClick={onClose}>Cancelar</Button>

          <Button onClick={handleSave} disabled={views.length < 1 || menuName === ""} color="primary">
            {selectedMenu ? "Actualizar" : "Agregar"}
          </Button>
        </DialogActions>
      </Dialog>
    </DndProvider>
  );
};

export default EditViewModal;
